import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, BarChart3 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { tInput, tLabel, tBtn, useTrackEvents, CsvImportButton } from "./trackUi";

export default function TrackAthletes({ orgId, gender, canWrite, onOpenAthlete }) {
  const events = useTrackEvents();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", grad_year: "", grade: "", events: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/track/athletes?organization_id=${orgId}&gender=${gender}`); setList(data); }
    catch { toast.error("Failed to load roster."); } finally { setLoading(false); }
  }, [orgId, gender]);
  useEffect(() => { load(); }, [load]);

  const toggleEvent = (c) => setForm((f) => ({ ...f, events: f.events.includes(c) ? f.events.filter((e) => e !== c) : [...f.events, c] }));
  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/track/athletes", { ...form, gender, organization_id: orgId });
      toast.success("Athlete added."); setForm({ first_name: "", last_name: "", grad_year: "", grade: "", events: [] }); setShowAdd(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (a) => { if (!window.confirm(`Remove ${a.name}? Their results will be deleted.`)) return; try { await api.delete(`/track/athletes/${a.id}`); load(); } catch { toast.error("Delete failed."); } };

  const title = gender === "F" ? "Female Roster" : "Male Roster";
  return (
    <div data-testid={`track-roster-${gender}`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="font-display font-black uppercase tracking-tighter text-4xl">{title}</h1><p className="text-white/40 text-sm mt-1" data-testid={`roster-count-${gender}`}>{list.length} athletes</p></div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <CsvImportButton testid={`import-athletes-${gender}`} endpoint="/track/import/athletes" orgId={orgId} label="Import CSV" template="Columns: First Name, Last Name, Gender, Grad Year, Events" onDone={load} />
            <button data-testid={`add-athlete-${gender}`} onClick={() => setShowAdd((v) => !v)} className={tBtn}>{showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "Add Athlete"}</button>
          </div>
        )}
      </div>

      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-6 space-y-4" data-testid="athlete-form">
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className={tLabel}>First *</label><input data-testid="ath-first" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={tInput} /></div>
            <div><label className={tLabel}>Last *</label><input data-testid="ath-last" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className={tInput} /></div>
            <div><label className={tLabel}>Grad Year</label><input value={form.grad_year} onChange={(e) => setForm({ ...form, grad_year: e.target.value })} className={tInput} /></div>
            <div><label className={tLabel}>Grade</label><input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className={tInput} /></div>
          </div>
          <div>
            <label className={tLabel}>Events</label>
            <div className="flex flex-wrap gap-1.5">
              {events.map((ev) => <button type="button" key={ev.code} onClick={() => toggleEvent(ev.code)} className={`px-2.5 py-1 text-xs border transition-colors ${form.events.includes(ev.code) ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>{ev.name}</button>)}
            </div>
          </div>
          <button data-testid="ath-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Save Athlete</button>
        </form>
      )}

      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : list.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid={`roster-empty-${gender}`}>No athletes yet. {canWrite ? "Add one above or import a CSV." : ""}</div>
        : (
          <div className="border border-white/10 bg-[#121212] overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-white/40">{["Name", "Grad", "Grade", "Events", ""].map((h, i) => <th key={i} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id} data-testid={`roster-row-${a.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3"><button data-testid={`open-athlete-${a.id}`} onClick={() => onOpenAthlete?.(a)} className="font-bold hover:underline flex items-center gap-2"><BarChart3 size={14} className="text-white/40" />{a.name}</button></td>
                    <td className="px-4 py-3 text-white/60">{a.grad_year || "—"}</td>
                    <td className="px-4 py-3 text-white/60">{a.grade || "—"}</td>
                    <td className="px-4 py-3 text-white/60">{(a.events || []).join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-right">{canWrite && <button data-testid={`del-athlete-${a.id}`} onClick={() => del(a)} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
